import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { IntegrationProviderMeta, SlackApi } from 'api/integrations';
import {
  SlackChannel,
  SlackConfig,
  SlackNotificationEvent,
  SlackProjectSettings,
  SlackSettingsMeta,
  SlackUser,
} from 'api/integrations/slack/slack.types';
import { fiveMinutes } from './constants';

const defaultOptions = {
  retry: 1,
  refetchOnWindowFocus: false,
  staleTime: fiveMinutes,
};

export const GET_SLACK_AUTH_META = 'GET_SLACK_AUTH_META';

export const useQuerySlackAuthMeta = (
  options: UseQueryOptions<
    unknown,
    unknown,
    IntegrationProviderMeta,
    typeof GET_SLACK_AUTH_META
  > = {},
): UseQueryResult<IntegrationProviderMeta> =>
  useQuery(GET_SLACK_AUTH_META, () => SlackApi.getSlackAuthMeta(), {
    ...defaultOptions,
    ...options,
  });

export const GET_SLACK_CONFIG = 'GET_SLACK_CONFIG';

export const getUseQuerySlackConfigKey = (): typeof GET_SLACK_CONFIG =>
  GET_SLACK_CONFIG;

export const useQuerySlackConfig = (
  options: UseQueryOptions<
    unknown,
    unknown,
    SlackConfig[],
    typeof GET_SLACK_CONFIG
  > = {},
): UseQueryResult<SlackConfig[]> =>
  useQuery(getUseQuerySlackConfigKey(), () => SlackApi.getSlackConfig(), {
    ...defaultOptions,
    ...options,
  });

export const useMutateSlackDisconnect = (
  options: UseMutationOptions<void, unknown, unknown> = {},
): UseMutationResult<unknown, unknown, number> =>
  useMutation(
    (integrationId: number) =>
      SlackApi.disconnectSlackIntegration(integrationId),
    { ...defaultOptions, ...options },
  );

export const GET_SLACK_CHANNELS_BY_ID = 'GET_SLACK_CHANNELS_BY_ID';

export const getUseQuerySlackChannelsKey = (
  integrationId: number,
): [typeof GET_SLACK_CHANNELS_BY_ID, number] => [
  GET_SLACK_CHANNELS_BY_ID,
  integrationId,
];

export const useQuerySlackChannels = (
  integrationId: number,
  options: UseQueryOptions<
    unknown,
    unknown,
    SlackChannel[],
    [typeof GET_SLACK_CHANNELS_BY_ID, number]
  > = {},
): UseQueryResult<SlackChannel[]> =>
  useQuery(
    getUseQuerySlackChannelsKey(integrationId),
    () => SlackApi.getSlackChannels(integrationId),
    { ...defaultOptions, ...options },
  );

export const GET_SLACK_USERS_BY_ID = 'GET_SLACK_USERS_BY_ID';

export const getUseQuerySlackUsersKey = (
  integrationId: number,
): [typeof GET_SLACK_USERS_BY_ID, number] => [
  GET_SLACK_USERS_BY_ID,
  integrationId,
];

export const useQuerySlackUsers = (
  integrationId: number,
  options: UseQueryOptions<
    unknown,
    unknown,
    SlackUser[],
    [typeof GET_SLACK_USERS_BY_ID, number]
  > = {},
): UseQueryResult<SlackUser[]> =>
  useQuery(
    getUseQuerySlackUsersKey(integrationId),
    () => SlackApi.getSlackUsers(integrationId),
    { ...defaultOptions, ...options },
  );

export const useMutateSlackSettings = (
  integrationId: number,
  options: UseMutationOptions<
    SlackSettingsMeta,
    string,
    SlackSettingsMeta,
    SlackConfig[]
  > = {},
): UseMutationResult<SlackSettingsMeta, string, SlackSettingsMeta> =>
  useMutation(
    (payload: SlackSettingsMeta) =>
      SlackApi.updateSlackSettings(integrationId, payload),
    { ...defaultOptions, ...options },
  );

export const GET_SLACK_NOTIFICATIONS_SETTINGS =
  'GET_SLACK_NOTIFICATIONS_SETTINGS';

export const getUseSlackNotificationsSettingsKey = (
  integrationId: number,
): [typeof GET_SLACK_NOTIFICATIONS_SETTINGS, number] => [
  GET_SLACK_NOTIFICATIONS_SETTINGS,
  integrationId,
];

export const useQuerySlackNotificationsSettings = (
  integrationId: number,
  options: UseQueryOptions<
    unknown,
    unknown,
    SlackNotificationEvent[],
    [typeof GET_SLACK_NOTIFICATIONS_SETTINGS, number]
  > = {},
): UseQueryResult<SlackNotificationEvent[]> =>
  useQuery(
    getUseSlackNotificationsSettingsKey(integrationId),
    () => SlackApi.getNotificationsConfig(integrationId),
    { ...defaultOptions, ...options },
  );

export const useMutateSlackNotificationsSettings = (
  integrationId: number,
  options: UseMutationOptions<
    SlackNotificationEvent[],
    unknown,
    SlackNotificationEvent[],
    unknown
  > = {},
): UseMutationResult<
  SlackNotificationEvent[],
  unknown,
  SlackNotificationEvent[],
  unknown
> =>
  useMutation(
    (payload: SlackNotificationEvent[]) =>
      SlackApi.updateNotificationConfig(integrationId, payload),
    { ...defaultOptions, ...options },
  );

export const SLACK_PROJECT_NOTIFICATION_SETTINGS =
  'SLACK_PROJECT_NOTIFICATION_SETTINGS';

export const getSlackProjectNotificationQueryKey = (
  projectId: number,
): [typeof SLACK_PROJECT_NOTIFICATION_SETTINGS, number] => [
  SLACK_PROJECT_NOTIFICATION_SETTINGS,
  projectId,
];

export const useQueryProjectSlackNotificationSettings = (
  projectId: number,
  options: UseQueryOptions<
    unknown,
    unknown,
    SlackProjectSettings,
    [typeof SLACK_PROJECT_NOTIFICATION_SETTINGS, number]
  > = {},
): UseQueryResult<SlackProjectSettings> =>
  useQuery(
    getSlackProjectNotificationQueryKey(projectId),
    () => SlackApi.getProjectSlackSettings(projectId),
    { ...defaultOptions, ...options },
  );

export const useMutateProjectSlackNotificationSettings = (
  projectId: number,
  options: UseMutationOptions<
    SlackProjectSettings,
    unknown,
    SlackProjectSettings,
    unknown
  > = {},
): UseMutationResult<
  SlackProjectSettings,
  unknown,
  SlackProjectSettings,
  unknown
> =>
  useMutation(
    (payload: SlackProjectSettings) =>
      SlackApi.updateProjectSlackSettings(projectId, payload),
    { ...defaultOptions, ...options },
  );
